import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";

/**
 * Homepage feature — Panini FIFA World Cup 2026 Black Border 1 of 1 Tracker.
 * Two-column premium collectible-style card. Reuses the canonical
 * NOT-SURFACED placeholder artwork already used across /black-1of1.
 * All statistics come from the same lightweight `/stats/summary` endpoint
 * that powers the tracker hero — no numbers are hard-coded.
 */
export default function BlackOneOfOneFeatureCard() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/black-1of1/stats/summary").then(r => r.data);
        if (!cancelled) setS(r);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loading = !s && !err;
  const surfaced = loading ? null : s?.found;
  const notSurfaced = loading ? null : s?.not_surfaced;
  const pct = loading ? null : s?.percent;

  return (
    <Link
      to="/black-1of1"
      data-testid="home-black-1of1-card"
      aria-label="Explore the Panini FIFA World Cup 2026 Black Border 1 of 1 Tracker"
      className="group relative block rounded-2xl overflow-hidden border-2 border-[#d4af37]/40 hover:border-[#d4af37] transition"
    >
      {/* Layered background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0b0b0d] via-[#141419] to-[#0b0b0d]" />
      <div className="absolute -top-16 -right-24 w-[420px] h-[420px] bg-[#d4af37]/[0.10] rounded-full blur-[110px] pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-[360px] h-[360px] bg-[#16a34a]/[0.08] rounded-full blur-[100px] pointer-events-none" />

      <div className="relative p-5 md:p-8 text-white">
        <div className="grid md:grid-cols-[1fr_minmax(0,240px)] gap-6 md:gap-10 items-center">
          {/* Copy + stats column */}
          <div className="min-w-0 order-2 md:order-1">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.28em] text-[#d4af37] font-mono font-semibold mb-2">
              <Sparkles size={14} /> Panini FIFA World Cup 2026
            </div>
            <h3 className="font-display text-2xl md:text-4xl leading-[1.05] text-white">
              Black Border <span className="text-[#d4af37]">1 of 1</span> Tracker
            </h3>
            <p className="text-white/75 text-sm md:text-base mt-2 max-w-lg leading-relaxed">
              The rarest hunt in the 2026 sticker collection. Only one Black Border copy of each eligible sticker exists. See what has surfaced — and what may still be hiding inside unopened packs.
            </p>

            {/* Stats row */}
            <div className="mt-5 grid grid-cols-2 gap-3 max-w-md" data-testid="home-black-1of1-stat">
              <div className="rounded-lg border border-[#22c55e]/30 bg-[#22c55e]/[0.06] px-3 py-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/60 font-semibold">Surfaced</div>
                <div className="font-display text-2xl md:text-3xl font-bold text-[#22c55e] leading-none mt-1" data-testid="home-stat-found">
                  {loading ? <span className="inline-block h-7 w-10 rounded bg-white/10 animate-pulse" /> : err ? "—" : surfaced}
                </div>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/60 font-semibold">Not yet surfaced</div>
                <div className="font-display text-2xl md:text-3xl font-bold text-white leading-none mt-1" data-testid="home-stat-notsurfaced">
                  {loading ? <span className="inline-block h-7 w-12 rounded bg-white/10 animate-pulse" /> : err ? "—" : notSurfaced}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4 h-2.5 rounded-full bg-white/[0.06] border border-white/10 overflow-hidden relative max-w-md" data-testid="home-black-1of1-progress">
              {loading ? (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.08] to-transparent animate-pulse" />
              ) : (
                <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#16a34a] via-[#22c55e] to-[#d4af37] transition-all duration-700 ease-out"
                       style={{ width: `${Math.max(0.6, Math.min(100, pct ?? 0))}%` }} />
              )}
            </div>
            <div className="mt-2 text-[11px] font-mono uppercase tracking-widest text-[#d4af37] font-semibold">
              {loading ? <span className="inline-block h-3 w-24 rounded bg-white/10 animate-pulse" /> : err ? "" : `${pct}% surfaced`}
            </div>

            {/* CTA */}
            <div className="mt-5 inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-[#d4af37] group-hover:gap-3 transition-all font-bold">
              Explore the 1 of 1 Tracker <ArrowRight size={16} />
            </div>
          </div>

          {/* Sticker column */}
          <div className="relative order-1 md:order-2 flex justify-center md:justify-end">
            <div className="relative w-[180px] sm:w-[210px] md:w-[240px] aspect-[3/4] rounded-xl bg-black p-[3px] shadow-[0_20px_60px_-10px_rgba(212,175,55,0.35)]">
              <img
                src="/textures/not-surfaced-card.webp"
                alt="Panini FIFA World Cup 2026 Black Border 1 of 1 not yet surfaced"
                loading="lazy"
                data-testid="home-black-1of1-sticker-img"
                className="absolute inset-[3px] w-[calc(100%-6px)] h-[calc(100%-6px)] object-contain bg-black rounded-lg"
              />
              <div className="absolute -top-2 -right-2 md:-top-3 md:-right-3 bg-[#d4af37] text-black text-[10px] md:text-[11px] font-mono font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-lg">
                Only one exists
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
